import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ReportingWidget } from "./reportingwidget";
import {
  dateFormats,
  convertNumber,
  formatNumber,
  isNumeric,
  formatNumberWithCommas,
  isEmptyObject,
  formatDate,
} from "../../lib/utils";
import {
  WIDGET_LINE_CHART,
  WIDGET_HBAR_CHART,
  WIDGET_HBARSTACKED_CHART,
  WIDGET_VBAR_CHART,
  WIDGET_VBARSTACKED_CHART,
  WIDGET_AREA_CHART,
  WIDGET_PIE_CHART,
  WIDGET_DOUGHNUT_CHART,
  WIDGET_FUNNEL_CHART,
  WIDGET_WATERFALL_CHART,
  WIDGET_RANGEAREA_CHART,
  WIDGET_RANGEBAR_CHART,
  chartLegendPositions,
  lineChartStyles,
  isMinMaxChart,
} from "./utils";

import {
  Chart,
  ChartArea,
  ChartPlotArea,
  ChartTitle,
  ChartSeries,
  ChartSeriesLabelsFrom,
  ChartSeriesLabelsTo,
  ChartSeriesItem,
  ChartSeriesItemTooltip,
  ChartCategoryAxis,
  ChartCategoryAxisTitle,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeriesLabels,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTooltip,
} from "@progress/kendo-react-charts";
import { groupBy } from "@progress/kendo-data-query";
import { useTranslation } from "react-i18next";

const seriesColors = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#393b79",
  "#637939",
  "#8c6d31",
  "#843c39",
  "#7b4173",
  "#5254a3",
  "#9c9ede",
  "#6b6ecf",
  "#e7ba52",
  "#bd9e39",
];
const GenericChartWidgetContent = (props) => {
  const {
    id,
    widgetTypeId,
    categoryFieldId,
    valueFieldId,
    secondaryFieldId,
    sortFieldId,
    sortFieldDirectionId,
    aggregate,
    defaultPosition,
    legendPosition,
    lineStyle,
    topDirectionId,
    topCount,
    data,
    schema,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const { t } = useTranslation();

  const _isMinMaxChart = isMinMaxChart(widgetTypeId);

  let legendPositionObj = chartLegendPositions.find(
    (e) => e.id == legendPosition
  );
  let legendPositionArr = legendPositionObj.title.split(" ");
  let position = legendPositionArr[0].toLowerCase(); //ex. bottom
  let orientation = legendPositionArr[1].toLowerCase(); //ex. horizontal

  let categoryFieldName = schema.find((e) => e.Id == categoryFieldId).Name;
  let valueFieldName = schema.find((e) => e.Id == valueFieldId).Name;
  let secondaryFieldName = secondaryFieldId
    ? schema.find((e) => e.Id == secondaryFieldId).Name
    : "";
  let lineStyleTitle =
    widgetTypeId == WIDGET_LINE_CHART
      ? lineChartStyles.find((e) => e.id == lineStyle).title.toLowerCase()
      : "";

  const minMaxTooltipRenderer = (chartData) => (prps) => {
    //valid only when we have min-max && secondary field
    if (!secondaryFieldName) return undefined;

    const categoryName = prps.point.category;
    const item = chartData.find((e) => e[categoryFieldName] == categoryName);
    if (!item) return undefined;

    let minText =
      item.min === null || item.min === undefined
        ? "Null"
        : isNumeric(item.min.toString())
        ? formatNumberWithCommas(item.min, 2)
        : "Null";
    let maxText =
      item.min === null || item.min === undefined
        ? "Null"
        : isNumeric(item.max.toString())
        ? formatNumberWithCommas(item.max, 2)
        : "Null";

    return (
      <div>
        <div>
            {t("extras.max")}: {item.secondaryMax} = {maxText}
        </div>
        <div>
            {t("extras.min")}: {item.secondaryMin} = {minText}
        </div>
      </div>
    );
  };

  const transformTwoD = () => {
    if (!data || data.length < 1) return [];

    let categoryField = schema.find((e) => e.Id == categoryFieldId);
    //console.log(categoryField)
    let categoryFieldName = categoryField.Name;
    let categoryFieldDataType = categoryField.DataTypeId;
    let valueFieldName = schema.find((e) => e.Id == valueFieldId).Name;
    let sortFieldName = sortFieldId
      ? schema.find((e) => e.Id == sortFieldId).Name
      : "";

    let result = [];
    let temp = data;

    //sort if need to
    if (sortFieldId && sortFieldId > 0)
      temp = [...data].sort((a, b) =>
        a[sortFieldName] > b[sortFieldName]
          ? -sortFieldDirectionId
          : sortFieldDirectionId
      );

    //group by and flat
    let grp;
    if (_isMinMaxChart) {
      if (!secondaryFieldId) {
        grp = groupBy(temp, [
          {
            field: categoryFieldName,
            aggregates: [
              { field: valueFieldName, aggregate: "min" },
              { field: valueFieldName, aggregate: "max" },
            ],
          },
        ]);

        if (grp && grp.length > 0) {
          for (let g in grp) {
            let v = grp[g].value;

            if (
              v != null &&
              (categoryFieldDataType == 4 || categoryFieldDataType == 5)
            ) {
              if (categoryFieldDataType == 4) v = formatDate(v, "mmm-dd-yyyy");
              else v = formatDate(v, "mmm-dd-yyyy HH:MM");
            }

            if (categoryFieldDataType == 3 && v == null) v = "Null";

            result.push({
              [categoryFieldName]: v,
              min: convertNumber(grp[g].aggregates[valueFieldName].min, 2),
              max: convertNumber(grp[g].aggregates[valueFieldName].max, 2),
            });
          }

          return result;
        } else return temp;
      } else {
        //min-max && secondary
        const secondaryFieldDataType = schema.find(
          (e) => e.Id == secondaryFieldId
        ).DataTypeId;

        grp = groupBy(temp, [
          {
            field: categoryFieldName,
            aggregates: [{ field: valueFieldName, aggregate: aggregate }],
          },
          {
            field: secondaryFieldName,
            aggregates: [{ field: valueFieldName, aggregate: aggregate }],
          },
        ]);

        if (grp && grp.length > 0) {
          for (let g in grp) {
            let obj = {
              [categoryFieldName]: "",
              secondaryMin: "",
              secondaryMax: "",
              min: null,
              max: null,
            };

            let v = grp[g].value;

            if (
              v != null &&
              (categoryFieldDataType == 4 || categoryFieldDataType == 5)
            ) {
              if (categoryFieldDataType == 4) v = formatDate(v, "mmm-dd-yyyy");
              else v = formatDate(v, "mmm-dd-yyyy HH:MM");
            }

            if (categoryFieldDataType == 3 && v == null) v = "Null";

            obj[categoryFieldName] = v;

            for (let item of grp[g].items) {
              //value below is the sum (i.e. agg) of secondary in the current grp
              let value = item.aggregates[valueFieldName][aggregate];

              if (value != null && (obj.min == null || obj.min > value)) {
                //if at least one non-null value exists, it'll be the min even if there are null values
                obj.min = convertNumber(value, 2);
                obj.secondaryMin =
                  item.value == null && secondaryFieldDataType == 3
                    ? "Null"
                    : item.value;
              }

              if (value != null && (obj.max == null || obj.max < value)) {
                //if at least one non-null value exists, it'll be the max even if there are null values
                obj.max = convertNumber(value, 2);
                obj.secondaryMax =
                  item.value == null && secondaryFieldDataType == 3
                    ? "Null"
                    : item.value;
              }
            }

            result.push(obj);
          }

          return result;
        } else return temp;
      }
    } else {
      //not min max
      grp = groupBy(temp, [
        {
          field: categoryFieldName,
          aggregates: [{ field: valueFieldName, aggregate: aggregate }],
        },
      ]);

      if (grp && grp.length > 0) {
        for (let g in grp) {
          let v = grp[g].value;

          if (
            v != null &&
            (categoryFieldDataType == 4 || categoryFieldDataType == 5)
          ) {
            if (categoryFieldDataType == 4) v = formatDate(v, "mmm-dd-yyyy");
            else v = formatDate(v, "mmm-dd-yyyy HH:MM:ss");
          }

          if (categoryFieldDataType == 3 && v == null) v = "Null";

          //console.log(grp[g], v, valueFieldName, aggregate, convertNumber(grp[g].aggregates[valueFieldName][aggregate],2))
          result.push({
            [categoryFieldName]: v,
            [valueFieldName]: convertNumber(
              grp[g].aggregates[valueFieldName][aggregate],
              2
            ),
          });
        }
      }

      if (
        topDirectionId &&
        isNumeric(topDirectionId.toString()) &&
        topCount &&
        isNumeric(topCount.toString())
      ) {
        let _topDirectionId = convertNumber(topDirectionId, 0);
        let _topCount = convertNumber(topCount, 0);

        if (_topCount > 0) {
          _topDirectionId = _topDirectionId == 1 ? -1 : 1; //topDirection=1 => Bottom (i.e. desc) => -1 and below becomes 1
          let result2 = [...result];

          temp = result2
            .sort((a, b) =>
              a[valueFieldName] > b[valueFieldName]
                ? -_topDirectionId
                : _topDirectionId
            )
            .filter((e, index) => index < topCount);

          return result.filter((e) => {
            if (
              temp.find(
                (t) =>
                  t[valueFieldName] == e[valueFieldName] &&
                  t[categoryFieldName] == e[categoryFieldName]
              )
            )
              return true;

            return false;
          });
        }
      }

      return result;
    }
  };

  const transformThreeD = () => {
    if (!data || data.length < 1) return [];

    let sorted = [];
    let temp = [];

    let categoryField = schema.find((e) => e.Id == categoryFieldId);
    let categoryFieldName = categoryField.Name;
    let categoryFieldDataType = categoryField.DataTypeId;
    let valueField = schema.find((e) => e.Id == valueFieldId);
    let valueFieldName = valueField.Name;
    let secondaryField = schema.find((e) => e.Id == secondaryFieldId);
    let secondaryFieldName = secondaryField.Name;
    let secondaryFieldDataType = secondaryField.DataTypeId;
    let sortFieldName = sortFieldId
      ? schema.find((e) => e.Id == sortFieldId).Name
      : "";

    //sort if need to
    if (sortFieldId && sortFieldId > 0)
      sorted = [...data].sort((a, b) =>
        a[sortFieldName] > b[sortFieldName]
          ? -sortFieldDirectionId
          : sortFieldDirectionId
      );
    else sorted = data;

    //get unique groups
    let uniqueCategories = [
      ...new Set(sorted.map((e) => e[categoryFieldName])),
    ];

    if (categoryFieldDataType == 4)
      uniqueCategories = uniqueCategories.map((d) =>
        formatDate(d, "mmm-dd-yyyy")
      );
    else if (categoryFieldDataType == 5)
      uniqueCategories = uniqueCategories.map((d) =>
        formatDate(d, "mmm-dd-yyyy HH:MM")
      );

    //end of get unique groups

    //group by and flat
    let grp = groupBy(sorted, [
      {
        field: categoryFieldName,
        aggregates: [{ field: valueFieldName, aggregate: aggregate }],
      },
      {
        field: secondaryFieldName,
        aggregates: [{ field: valueFieldName, aggregate: aggregate }],
      },
    ]);

    if (
      topDirectionId &&
      isNumeric(topDirectionId.toString()) &&
      topCount &&
      isNumeric(topCount.toString())
    ) {
      let _topDirectionId = convertNumber(topDirectionId, 0);
      let _topCount = convertNumber(topCount, 0);

      if (_topCount > 0) {
        _topDirectionId = _topDirectionId == 1 ? -1 : 1;

        for (let g in grp) {
          let _grp = grp[g];
          _grp.items = _grp.items
            .sort((a, b) =>
              a[valueFieldName] > b[valueFieldName]
                ? -_topDirectionId
                : _topDirectionId
            )
            .filter((e, index) => index < topCount);
        }
      }
    }

    if (grp && grp.length > 0) {
      for (let g in grp) {
        for (let i in grp[g].items) {
          let group = grp[g];
          let item = grp[g].items[i];

          let catFieldName = group.value;
          if (categoryFieldDataType == 3 && group.value == null)
            catFieldName = "Null";
          else if (
            catFieldName != null &&
            (categoryFieldDataType == 4 || categoryFieldDataType == 5)
          ) {
            if (categoryFieldDataType == 4)
              catFieldName = formatDate(catFieldName, "mmm-dd-yyyy");
            else catFieldName = formatDate(catFieldName, "mmm-dd-yyyy HH:MM");
          }

          let secFieldName = item.value;
          if (secondaryFieldDataType == 3 && item.value == null)
            secFieldName = "Null";
          else if (
            secFieldName != null &&
            (secondaryFieldDataType == 4 || secondaryFieldDataType == 5)
          ) {
            if (secondaryFieldDataType == 4)
              secFieldName = formatDate(secFieldName, "mmm-dd-yyyy");
            else secFieldName = formatDate(secFieldName, "mmm-dd-yyyy HH:MM");
          }

          temp.push({
            [categoryFieldName]: catFieldName,
            [secondaryFieldName]: secFieldName,
            [valueFieldName]: convertNumber(
              item.aggregates[valueFieldName][aggregate],
              2
            ),
          });
        }
      }

      let z = groupBy(temp, [{ field: secondaryFieldName }]);
      return { data: z, distinctCategories: uniqueCategories };
      //return z;
    } else {
      return { data: sorted, distinctCategories: uniqueCategories };
    }
  };

  const [processedData, setProcessedData] = useState([]);
  const [distinctCategories, setDistinctCategories] = useState([]);

  useEffect(() => {
    let grpData = [];
    if (!_isMinMaxChart && secondaryFieldId) grpData = transformThreeD();

    setProcessedData((state) => {
      if (_isMinMaxChart) return transformTwoD();

      if (!secondaryFieldId)
        //2-dimension
        return transformTwoD();
      else {
        return grpData.data; // transformThreeD();
      }
    });

    setDistinctCategories(grpData.distinctCategories);
  }, [
    data,
    categoryFieldId,
    valueFieldId,
    secondaryFieldId,
    sortFieldId,
    sortFieldDirectionId,
    aggregate,
    topDirectionId,
    topCount,
    defaultPosition,
    legendPosition,
    lineStyle,
  ]);

  // const handleChartRefresh = (chartOptions, themeOptions, chartInstance) => {
  //     //if (refreshChart) {
  //       console.log("REFREHING")
  //       chartInstance.setOptions(chartOptions, themeOptions);
  //     //}
  //   };

  return (
    <Fragment>
      {processedData && processedData.length > 0 && (
        <div
          className={`generic-chart-content generic-chart-content-${id} ${widgetTypeId}-chart-content`}
          style={{}}
        >
          {(widgetTypeId == WIDGET_LINE_CHART ||
            widgetTypeId == WIDGET_AREA_CHART) && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              {secondaryFieldId && (
                <Fragment>
                  <ChartLegend
                    position={position}
                    orientation={orientation}
                    labels={{ color: currentTheme.componentText }}
                    inactiveItems={{
                      markers: { visible: true },
                      labels: { color: currentTheme.componentText },
                    }} // keep labels visible
                    onItemClick={(e) => e.preventDefault()} // stop toggling
                  />
                  <ChartSeries>
                    {processedData.map((e, index) => {
                      return (
                        <ChartSeriesItem
                          key={index}
                          markers={{ visible: lineStyle == 1 ? true : false }}
                          style={lineStyleTitle}
                          data={e.items}
                          name={e.value}
                          field={valueFieldName}
                          categoryField={categoryFieldName}
                          type={
                            widgetTypeId == WIDGET_LINE_CHART ? "line" : "area"
                          }
                          tooltip={{
                            visible: true,
                          }}
                        ></ChartSeriesItem>
                      );
                    })}
                  </ChartSeries>
                </Fragment>
              )}
              {!secondaryFieldId && (
                <ChartSeries>
                  {[
                    <ChartSeriesItem
                      key={id}
                      markers={{ visible: lineStyle == 1 ? true : false }}
                      style={lineStyleTitle}
                      data={processedData}
                      field={valueFieldName}
                      categoryField={categoryFieldName}
                      type={widgetTypeId == WIDGET_LINE_CHART ? "line" : "area"}
                      tooltip={{
                        visible: true,
                      }}
                    ></ChartSeriesItem>,
                  ]}
                </ChartSeries>
              )}
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  labels={{
                    rotation: "auto",
                    visible: true,
                  }}
                  categories={
                    distinctCategories && distinctCategories.length > 0
                      ? distinctCategories
                      : undefined
                  }
                  majorGridLines={{color: "#eee", visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  labels={{ visible: true, format: "{0}" }}
                  majorGridLines={{color: "#eee", visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartValueAxis>
            </Chart>
          )}
          {(widgetTypeId == WIDGET_HBAR_CHART ||
            widgetTypeId == WIDGET_HBARSTACKED_CHART ||
            widgetTypeId == WIDGET_VBAR_CHART ||
            widgetTypeId == WIDGET_VBARSTACKED_CHART) && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              {secondaryFieldId && (
                <Fragment>
                  <ChartLegend
                    position={position}
                    orientation={orientation}
                    labels={{ color: currentTheme.componentText }}
                    inactiveItems={{
                      markers: { visible: true },
                      labels: { color: currentTheme.componentText },
                    }} // keep labels visible
                    onItemClick={(e) => e.preventDefault()} // stop toggling
                  />
                  <ChartSeries>
                    {processedData.map((e, index) => (
                      <ChartSeriesItem
                        key={index}
                        gap={2}
                        spacing={0.25}
                        data={e.items}
                        name={e.value}
                        field={valueFieldName}
                        categoryField={categoryFieldName}
                        type={
                          widgetTypeId == WIDGET_HBAR_CHART ||
                          widgetTypeId == WIDGET_HBARSTACKED_CHART
                            ? "bar"
                            : "column"
                        }
                        stack={
                          widgetTypeId == WIDGET_HBARSTACKED_CHART ||
                          widgetTypeId == WIDGET_VBARSTACKED_CHART
                        }
                        tooltip={{
                          visible: true,
                        }}
                      ></ChartSeriesItem>
                    ))}
                  </ChartSeries>
                </Fragment>
              )}
              {!secondaryFieldId && (
                <ChartSeries>
                  {[
                    <ChartSeriesItem
                      key={id}
                      lineStyle={lineStyleTitle}
                      data={processedData}
                      name={valueFieldName}
                      field={valueFieldName}
                      categoryField={categoryFieldName}
                      type={
                        widgetTypeId == WIDGET_HBAR_CHART ||
                        widgetTypeId == WIDGET_HBARSTACKED_CHART
                          ? "bar"
                          : "column"
                      }
                      stack={
                        widgetTypeId == WIDGET_HBARSTACKED_CHART ||
                        widgetTypeId == WIDGET_VBARSTACKED_CHART
                      }
                      tooltip={{
                        visible: true,
                      }}
                    ></ChartSeriesItem>,
                  ]}
                </ChartSeries>
              )}
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  labels={{
                    rotation: "auto",
                    visible: true,
                  }}
                  categories={
                    distinctCategories && distinctCategories.length > 0
                      ? distinctCategories
                      : undefined
                  }
                  majorGridLines={{ color: "#eee",visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  labels={{ visible: true, format: "{0}" }}
                  majorGridLines={{ color: "#eee",visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartValueAxis>
            </Chart>
          )}
          {widgetTypeId == WIDGET_PIE_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartLegend
                position={position}
                orientation={orientation}
                labels={{ color: currentTheme.componentText }}
                inactiveItems={{
                  markers: { visible: true },
                  labels: { color: currentTheme.componentText },
                }} // keep labels visible
                onItemClick={(e) => e.preventDefault()} // stop toggling
              />
              <ChartSeries>
                {[
                  <ChartSeriesItem
                    key={id}
                    type="pie"
                    data={processedData}
                    name={valueFieldName}
                    field={valueFieldName}
                    categoryField={categoryFieldName}
                  >
                    {[
                      <ChartSeriesLabels
                        key={id}
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (e.value === null || e.value === undefined)
                            return "Null";
                          return !isNumeric(e.value.toString())
                            ? "Null"
                            : formatNumber(e.value, 2, ",");
                        }}
                      />,
                    ]}
                  </ChartSeriesItem>,
                ]}
              </ChartSeries>
            </Chart>
          )}
          {widgetTypeId == WIDGET_DOUGHNUT_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartLegend
                position={position}
                orientation={orientation}
                labels={{ color: currentTheme.componentText }}
                inactiveItems={{
                  markers: { visible: true },
                  labels: { color: currentTheme.componentText },
                }} // keep labels visible
                onItemClick={(e) => e.preventDefault()} // stop toggling
              />
              <ChartSeries>
                {[
                  <ChartSeriesItem
                    key={id}
                    type="donut"
                    data={processedData}
                    name={valueFieldName}
                    color={currentTheme.componentText}
                    field={valueFieldName}
                    categoryField={categoryFieldName}
                  >
                    {[
                      <ChartSeriesLabels
                        key={id}
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (e.value === null || e.value === undefined)
                            return "Null";

                          return !isNumeric(e.value.toString())
                            ? "Null"
                            : formatNumber(e.value, 2, ",");
                        }}
                      />,
                    ]}
                  </ChartSeriesItem>,
                ]}
              </ChartSeries>
            </Chart>
          )}
          {widgetTypeId == WIDGET_FUNNEL_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartLegend
                position={position}
                orientation={orientation}
                labels={{ color: currentTheme.componentText }}
                inactiveItems={{
                  markers: { visible: true },
                  labels: { color: currentTheme.componentText },
                }} // keep labels visible
                onItemClick={(e) => e.preventDefault()} // stop toggling
              />
              <ChartSeries>
                {[
                  <ChartSeriesItem
                    key={id}
                    type="funnel"
                    data={processedData}
                    field={valueFieldName}
                    color={currentTheme.componentText}
                    categoryField={categoryFieldName}
                    neckRatio={0.3}
                    dynamicSlope={true}
                  >
                    {[
                      <ChartSeriesLabels
                        key={id}
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (e.value === null || e.value === undefined)
                            return "Null";
                          return !isNumeric(e.value.toString())
                            ? "Null"
                            : formatNumber(e.value, 2, ",");
                        }}
                      />,
                    ]}
                  </ChartSeriesItem>,
                ]}
              </ChartSeries>
            </Chart>
          )}
          {widgetTypeId == WIDGET_WATERFALL_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartLegend
                position={position}
                orientation={orientation}
                labels={{ color: currentTheme.componentText }}
                inactiveItems={{
                  markers: { visible: true },
                  labels: { color: currentTheme.componentText },
                }} // keep labels visible
                onItemClick={(e) => e.preventDefault()} // stop toggling
              />
              <ChartSeries>
                {[
                  <ChartSeriesItem
                    key={id}
                    type="waterfall"
                    data={processedData}
                    field={valueFieldName}
                    categoryField={categoryFieldName}
                    color={(point) =>
                      (point.dataItem[valueFieldName] == null ||
                        point.dataItem[valueFieldName] == null) < 0
                        ? currentTheme.error
                        : currentTheme.success
                    }
                  >
                    {<ChartSeriesLabels position="insideEnd" />}
                  </ChartSeriesItem>,
                ]}
              </ChartSeries>
              <ChartValueAxis>
                <ChartValueAxisItem
                  labels={{ visible: true, format: "{0}" }}
                  majorGridLines={{color: "#eee", visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartValueAxis>
            </Chart>
          )}
          {widgetTypeId == WIDGET_RANGEBAR_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartSeries>
                {
                  <ChartSeriesItem
                    type="rangeColumn"
                    data={processedData}
                    fromField="min"
                    toField="max"
                    categoryField={categoryFieldName}
                  >
                    <ChartSeriesLabels>
                      <ChartSeriesLabelsFrom
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (
                            e.value.from === null ||
                            e.value.from === undefined
                          )
                            return "Null";

                          return !isNumeric(e.value.from.toString())
                            ? "Null"
                            : e.value.from;
                        }}
                      />
                      <ChartSeriesLabelsTo
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (e.value.to === null || e.value.to === undefined)
                            return "Null";

                          return !isNumeric(e.value.to.toString())
                            ? "Null"
                            : e.value.to;
                        }}
                      />
                    </ChartSeriesLabels>
                  </ChartSeriesItem>
                }
              </ChartSeries>
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  labels={{
                    rotation: "auto",
                    visible: true,
                  }}
                  majorGridLines={{ color: "#eee",visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  labels={{ visible: true, format: "{0}" }}
                  majorGridLines={{color: "#eee", visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartValueAxis>
              <ChartTooltip
                visible={secondaryFieldId}
                render={minMaxTooltipRenderer(processedData)}
              />
            </Chart>
          )}
          {widgetTypeId == WIDGET_RANGEAREA_CHART && (
            <Chart
              style={{ color: currentTheme.componentText }}
              seriesColors={seriesColors}
            >
              <ChartArea background={currentTheme.componentBg} margin={10} />
              <ChartSeries>
                {
                  <ChartSeriesItem
                    type="rangeArea"
                    data={processedData}
                    fromField="min"
                    toField="max"
                    categoryField={categoryFieldName}
                  >
                    <ChartSeriesLabels>
                      <ChartSeriesLabelsFrom
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (
                            e.value.from === null ||
                            e.value.from === undefined
                          )
                            return "Null";

                          return !isNumeric(e.value.from.toString())
                            ? "Null"
                            : e.value.from;
                        }}
                      />
                      <ChartSeriesLabelsTo
                        color={currentTheme.componentText}
                        background="none"
                        content={(e, index) => {
                          if (e.value.to === null || e.value.to === undefined)
                            return "Null";

                          return !isNumeric(e.value.to.toString())
                            ? "Null"
                            : e.value.to;
                        }}
                      />
                    </ChartSeriesLabels>
                  </ChartSeriesItem>
                }
              </ChartSeries>
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  labels={{
                    rotation: "auto",
                    visible: true,
                  }}
                  majorGridLines={{ color: "#eee",visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  labels={{ visible: true, format: "{0}" }}
                  majorGridLines={{ color: "#eee",visible: true }}
                  line={{ visible: true }}
                  color={currentTheme?.primary || "#007bff"}
                />
              </ChartValueAxis>
              <ChartTooltip
                visible={secondaryFieldId}
                render={minMaxTooltipRenderer(processedData)}
              />
            </Chart>
          )}
        </div>
      )}
      <style>
        {`
                .generic-chart-content
                {
                    margin-top: 8px;
                    text-align: center;       
                    flex: 1;
                    display: flex;                           
                    align-items: center;         
                    height: calc(100% - 8px);
                    width: 100%;
                }
                    
                .generic-chart-content .k-chart
                {
                    height: calc(100% - 12px) !important;
                    width: 100% !important;
                }
                `}
      </style>
    </Fragment>
  );
};

export const GenericChartWidget = (props) => {
  const {
    id,
    widgetTypeId,
    mode,
    loaded,
    loading,
    errorText,
    canceled,
    backgroundColor,
    title,
    titleFontSize,
    titleFontIsBold,
    titleFontIsUnderline,
    titleFontIsItalic,
    titleFontColor,
    getContextMenu,
    onMenuItemClick,
    onWidgetSelect,
    onWidgetUnSelect,
    onDoubleClick,
    ...other
  } = props;

  return (
    <Fragment>
      <ReportingWidget
        id={id}
        widgetTypeId={widgetTypeId}
        dataSourceValueType={0}
        mode={mode}
        loaded={loaded}
        loading={loading}
        errorText={errorText}
        canceled={canceled}
        backgroundColor={backgroundColor}
        title={title}
        titleFontSize={titleFontSize}
        titleFontIsBold={titleFontIsBold}
        titleFontIsUnderline={titleFontIsUnderline}
        titleFontIsItalic={titleFontIsItalic}
        titleFontColor={titleFontColor}
        getContextMenu={getContextMenu}
        onMenuItemClick={onMenuItemClick}
        onWidgetSelect={onWidgetSelect}
        onWidgetUnSelect={onWidgetUnSelect}
        onDoubleClick={onDoubleClick}
      >
        <GenericChartWidgetContent {...props} />
      </ReportingWidget>
      <style>
        {`
                        
                    `}
      </style>
    </Fragment>
  );
};
